import { useEffect, useRef } from "react";
import { ResponsiveImg } from "@/components/responsive-img";
import { imgSizes } from "@/lib/images";

export function ParallaxImage({
  name,
  alt,
  speed = 0.14,
  className = "",
  imgClassName = "",
  priority = false,
  sizes = imgSizes.hero,
}: {
  name: string;
  alt: string;
  speed?: number;
  className?: string;
  imgClassName?: string;
  priority?: boolean;
  sizes?: string;
}) {
  const wrap = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let frame = 0;
    let idleId = 0;
    let timeoutId = 0;
    let started = false;

    const update = () => {
      frame = 0;
      const el = wrap.current;
      const im = inner.current;
      if (!el || !im) return;
      const rect = el.getBoundingClientRect();
      const progress = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight;
      im.style.transform = `translate3d(0, ${(progress * speed * 100).toFixed(2)}px, 0) scale(1.18)`;
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    const start = () => {
      if (started) return;
      started = true;
      update();
      window.addEventListener("scroll", onScroll, { passive: true });
      window.addEventListener("resize", onScroll);
    };

    if (typeof requestIdleCallback === "function") {
      idleId = requestIdleCallback(start, { timeout: 1500 });
    } else {
      timeoutId = window.setTimeout(start, 1);
    }

    return () => {
      if (idleId) cancelIdleCallback(idleId);
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [speed]);

  return (
    <div ref={wrap} className={`overflow-hidden ${className}`}>
      <ResponsiveImg
        ref={inner}
        name={name}
        sizes={sizes}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        {...(priority
          ? { fetchPriority: "high" as const, decoding: "sync" as const }
          : { decoding: "async" as const })}
        className={`h-full w-full scale-[1.18] object-cover will-change-transform ${imgClassName}`}
      />
    </div>
  );
}
